import { useDispatch, useSelector } from 'react-redux';
import { activeProfileSelector } from 'data/requests/activeProfile';
import { useCallback, useMemo } from 'react';
import { getItem, setItem } from '../../helpers/storage';
import {
    hotelBrandSelectionSelector,
    updateBrandSelection, updateClientSelection, updateHotelSelection,
} from '../../helpers/hotelBrandSelectionReducer';
import { IClient } from 'src/interfaces/client/client';
import { useHistory } from 'react-router';

import { refreshFeatureFlagsOnHotelChange, refreshFeatureFlagsOnBrandChange } from '../../helpers/featureFlags/featureFlagIntegrationNew';

const checkIfClientExists = (clientData, availableClients): boolean => {
    if (clientData && clientData.clientId) {
        const cId = clientData.clientId;
        return !!(availableClients.map(c => c.client).find(o => o.id === cId));
    }
    return false;
};

// tslint:disable-next-line:cyclomatic-complexity
export const useCustomerBrandHotelSelection = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const params = new URLSearchParams(history.location.search);
    const paramHotelId = params.get('hotelid');
    const paramBrandId = params.get('brandId');
    const profile = useSelector(activeProfileSelector);
    const brandSelectionFromReduxStorage = useSelector(hotelBrandSelectionSelector);
    const availableClientsTemp = profile && profile.clientList ? profile.clientList : [];
    const availableClients = availableClientsTemp.filter(aC => !!aC.client);
    const brandSelectionFromLocalStorage = getItem('brandHotelSelection');

    const isClientAvailableLocalStorage = checkIfClientExists(brandSelectionFromLocalStorage, availableClients);
    const isClientAvailableReduxStorage = checkIfClientExists(brandSelectionFromReduxStorage, availableClients);

    const fromLocalStorage = isClientAvailableLocalStorage ? brandSelectionFromLocalStorage : {
        ...brandSelectionFromLocalStorage, clientId: undefined,
    };
    const fromReduxStore = isClientAvailableReduxStorage ? brandSelectionFromReduxStorage : {
        ...brandSelectionFromReduxStorage, clientId: undefined,
    };

    const hotelBrandSelection = {
        ...fromLocalStorage,
        ...fromReduxStore,
    };

    let { brandId, hotelId } = hotelBrandSelection;
    let clientId;
    hotelId = paramHotelId ? parseInt(paramHotelId) : hotelId;
    brandId = paramBrandId ? parseInt(paramBrandId) : brandId;
    if (!!hotelBrandSelection.clientId) {
        clientId = hotelBrandSelection.clientId;
    }

    let selectedClient: IClient | undefined;
    if (availableClients.length > 0) {
        if (!clientId && availableClients[0].client) {
            clientId = availableClients[0].client.id;
        }
        selectedClient = availableClients.map(c => c.client).find(o => o.id === clientId) || availableClients[0].client;
    }

    const brands = profile && profile.brands ? profile.brands : [];

    const availableBrands = brands; // brands.filter(brand => brand.clientId === clientId);
    const selectedBrand = availableBrands.find(brand => brand.id === brandId) || availableBrands[0];
    selectedClient = selectedBrand && selectedBrand.clientId ? 
        availableClients.map(c => c.client).find(o => o.id === selectedBrand.clientId) || availableClients[0].client : undefined;

    const hotels = profile && profile.hotels ? profile.hotels : [];

    const availableHotels = selectedBrand
        ? selectedBrand.hotels.filter(hotel => hotel.clientId === selectedBrand.clientId)
        : hotels;

    const selectedHotel = availableHotels.find(h => h.id === hotelId) || availableHotels[0];
    const setBrandId = useCallback(async (brandIdUpdate?: number) => {
        setItem('brandHotelSelection', {
            ...hotelBrandSelection,
            brandId: brandIdUpdate,
        });
        dispatch(updateBrandSelection(brandIdUpdate));
        
        // Refresh feature flags after brand change
        try {
            await refreshFeatureFlagsOnBrandChange();
        } catch (error) {
            // tslint:disable-next-line:no-console
            console.error('Failed to refresh feature flags on brand change:', error);
        }
    }, [hotelBrandSelection]);

    const event: any = new Event('updateBrandHotelId');
    event.hotelId = selectedHotel && selectedHotel.id ? selectedHotel.id : 0;
    event.brandId = selectedBrand && selectedBrand.id ? selectedBrand.id : 0;
    // document.dispatchEvent(event);
    document.dispatchEvent(event);

    const setHotelId = useCallback(async (hotelIdUpdate?: number) => {
        setItem('brandHotelSelection', {
            ...hotelBrandSelection,
            hotelId: hotelIdUpdate,
        });
        dispatch(updateHotelSelection(hotelIdUpdate));
        
        // Refresh feature flags after hotel change
        try {
            await refreshFeatureFlagsOnHotelChange(hotelIdUpdate || 0);
        } catch (error) {
            // tslint:disable-next-line:no-console
            console.error('Failed to refresh feature flags on hotel change:', error);
        }
    }, [hotelBrandSelection]);

    const setClientId = useCallback((clientIdUpdate?: number) => {
        setItem('brandHotelSelection', {
            ...hotelBrandSelection,
            clientId: clientIdUpdate,
        });
        dispatch(updateClientSelection(clientIdUpdate));
    }, []);

    const selectedBrandId = selectedBrand ? selectedBrand.id : (selectedHotel ? selectedHotel.brandId : undefined);
    const selectedHotelId = selectedHotel ? selectedHotel.id : undefined;
    const selectedClientId = selectedClient ? selectedClient.id : undefined;

    return useMemo(() => ({
        availableClients,
        selectedClient,
        selectedClientId,
        availableBrands,
        selectedBrand,
        selectedBrandId,
        availableHotels,
        selectedHotel,
        selectedHotelId,
        setBrandId,
        setHotelId,
        setClientId,
    }), [availableBrands, selectedBrand, availableHotels, selectedHotel, availableClients, selectedClient]);
};
